import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, AlertTriangle, ListChecks, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

// Daily task progress for the current user. Reuses backend /tasks/my-progress (same compliance logic).
export function TaskProgress({ compact = false }) {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get("/tasks/my-progress").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (!data) return null;
  const { total, done, pending, late, compliance, by_shift = [] } = data;

  const state = late > 0 ? "late" : (total > 0 && done >= total) ? "done" : "normal";
  const tone = { late: "#DC2626", done: "#16A34A", normal: "#2563EB" }[state];
  const barBg = { late: "bg-[#DC2626]", done: "bg-[#16A34A]", normal: "bg-[#2563EB]" }[state];
  const support = total === 0 ? "No tienes tareas asignadas hoy."
    : late > 0 ? `${late} tarea${late > 1 ? "s" : ""} atrasada${late > 1 ? "s" : ""}`
    : done >= total ? "¡Todo listo por hoy!"
    : `Te quedan ${total - done} tarea${total - done > 1 ? "s" : ""} por completar hoy.`;
  const Icon = state === "done" ? CheckCircle2 : state === "late" ? AlertTriangle : ListChecks;

  return (
    <div data-testid="task-progress" className={`rounded-2xl border border-slate-200 bg-white ${compact ? "p-4" : "p-5"}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5" style={{ color: tone }} />
          <p className="font-display text-base font-semibold text-[#153D35]">Progreso de hoy</p>
        </div>
        <span data-testid="task-progress-count" className="tnum text-sm font-semibold" style={{ color: tone }}>{done} / {total}</span>
      </div>

      <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div data-testid="task-progress-bar" className={`h-full rounded-full transition-all duration-500 ${barBg}`} style={{ width: `${total ? compliance : 0}%` }} />
      </div>
      <p className="mt-2 text-sm text-[#68756F]">
        <span className="font-medium text-[#153D35]">{done} de {total} tareas completadas</span>
        {total > 0 && ` · ${compliance}%`}
      </p>
      <p data-testid="task-progress-support" className="text-sm" style={{ color: tone }}>{support}</p>

      {by_shift.length > 1 && (
        <div className="mt-3 space-y-2">
          {by_shift.map((s) => (
            <div key={s.shift_id} data-testid={`task-progress-shift-${s.shift_id}`}>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#68756F]">{s.shift_name}</span>
                <span className="tnum font-medium text-[#153D35]">{s.done} / {s.total} — {s.compliance}%</span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-[#2563EB]" style={{ width: `${s.total ? s.compliance : 0}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {pending + late > 0 && (
        <Button data-testid="task-progress-cta" variant="outline" onClick={() => nav("/app/tareas")}
          className="mt-4 h-11 w-full rounded-xl border-[#2563EB]/30 text-[#2563EB]">
          Ver tareas pendientes <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
